import React, { useEffect, useMemo, useState } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { resolveGymImageUrl } from '../utils/gyms';
import { makeldJsonSchema } from '../utils/ldJson';

import { fetchGym } from '../services/api/fetchGym';
import { trackProductImpressions } from '../utils/tracking';
import { track } from '../services/analytics';

import { useMediaQuery } from '../hooks';

import { Column, Faqs, Grid, Notifications, OpeningHours, Row } from '@husslehq/ui-library';
import { getHeaderBanner, HeaderBanner } from '@husslehq/api-client-ts';

import { Layout } from '../components/Layout/Layout';
import { LayoutMain } from '../components/Layout/Main';
import { GymHeader } from '../components/GymHeader/GymHeader';
import { GymIntro } from '../components/GymIntro/GymIntro';
import { Mosaic } from '../components/Mosaic/Mosaic';
import { MosaicMobile } from '../components/MosaicMobile/MosaicMobile';
import { Banner } from '../components/Banner/Banner';
import { SectionSubTitle } from '../components/SectionSubTitle/SectionSubTitle';
import { Divider } from '../components/Divider/Divider';
import { ResourceList } from '../components/ResourceList/ResourceList';
import { Description } from '../components/Description/Description';
import { Map } from '../components/Map/Map';
import { Stats } from '../components/Stats/Stats';
import { FeedbackBlock } from '../components/Feedback/FeedbackBlock';
import { PassPickerBlock } from '../components/PassPicker/PassPickerBlock';
import { Breadcrumbs } from '../components/Breadcrumbs/Breadcrumbs';
import { Chat } from '../components/Chat/Chat';
import { Info } from '../components/Icons';
import { HowToGetStartedCorporate } from '../components/HowToGetStartedCorporate/HowToGetStartedCorporate';
import { Testimonials } from '../components/Testimonials/Testimonials';

import gymData from './gym-data.json';
import testimonialsData from '../data/testimonials/corporate.json';

import { BaseGym } from '../types/Gym';
import { getUser } from '../services/authentication';
import { User } from '../types/authentication';

import styles from '../pages/Gym.module.scss';

export const GymDetailsPage: NextPage<{ data: BaseGym }> = ({ data: ssrData }) => {
  const { query, asPath: urlPath } = useRouter();
  const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX ?? '';
  const [gym, setGym] = useState(ssrData);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isCorporateUser, setIsCorporateUser] = useState(false);
  const [headerBanner, setHeaderBanner] = useState<HeaderBanner | undefined>(undefined);
  const isTablet = useMediaQuery(768, 'min');
  const HeaderBG = assetPrefix + '/img/gym-header-placeholder.jpg';
  const firstImage = gym.header && gym.header.url ? resolveGymImageUrl(gym.id, gym.header.url, '660x450') : HeaderBG;
  const mobileFirstImage =
    gym.header && gym.header.url ? resolveGymImageUrl(gym.id, gym.header.url, '385x265') : HeaderBG;

  const images = useMemo(() => {
    if (!gym) {
      return [];
    }

    return gym.photos
      .sort((a, b) => a.order - b.order)
      .map((photo) => ({
        url: resolveGymImageUrl(gym.id, photo.url),
        alt: gym.name,
      }));
  }, [gym]);

  const galleryImages = useMemo(() => {
    if (!gym) {
      return [];
    }

    return gym.photos
      .sort((a, b) => a.order - b.order)
      .map((photo) => ({
        original: resolveGymImageUrl(gym.id, photo.url, '940x680'),
        thumbnail: resolveGymImageUrl(gym.id, photo.url),
        imageSet: [
          {
            srcSet: resolveGymImageUrl(gym.id, photo.url, '940x680').replace(/\.[^/.]+$/, '.webp'),
            type: 'image/webp',
          },
        ],
      }));
  }, [gym]);

  const gymNotListedMessage = useMemo(() => {
    const message = 'This gym is currently not available at Hussle';

    if (user && user.hasMonthly) {
      return `${message}, please choose a new main gym for your Monthly+ pass`;
    }

    if (user && user.bonusCredit) {
      return `${message}, you have £${user.bonusCredit} to buy a pass at a different gym`;
    }

    return message;
  }, [user]);

  const recommendedPass = useMemo(() => {
    return query.recommendedPass;
  }, [query]);

  useEffect(() => {
    const cookies = document.cookie;

    getUser(cookies).then((tokenPayload) => {
      const { token, user } = tokenPayload;
      if (token) {
        fetchGym(String(gym.id), token).then((gymData) => {
          setGym(gymData);
          if (user) {
            setUser(user);
          }
        });
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const recommendedPass = urlParams.get('recommendedPass');
    trackProductImpressions(gym, recommendedPass);
  }, []);

  useEffect(() => {
    const hasCorporateCustomerSourceSegment = gym.user_customer_sources.includes('B2B Corporate');
    setIsCorporateUser(hasCorporateCustomerSourceSegment);
  }, [gym]);

  useEffect(() => {
    const cookies = document.cookie;
    getUser(cookies).then((tokenPayload) => {
      const { token } = tokenPayload;
      getHeaderBanner(urlPath, token, parseInt(gym.id, 10)).then((headerBanner) => {
        if (!headerBanner) return;

        setHeaderBanner(headerBanner);
      });
    });
  }, []);

  useEffect(() => {
    track({
      event: 'gym_spa',
      category: 'Venue Detail Page',
      action: 'Page Load',
      label: 'End of List',
      value: '{nonInteraction: true}',
    });
  }, []);

  return (
    <>
      <Layout headerBanner={headerBanner} showAppDownload user={user}>
        <Head>
          <title>{gym.name} | Hussle.com</title>
          <meta
            name="description"
            content={
              gym.name +
              ', read ' +
              gym.feedback.count +
              ' Hussle reviews. Visit the gym from £' +
              (gym.products && Math.min(...gym.products.map((p) => p.price)) / 100).toFixed(2)
            }
          />
          <meta
            name="keywords"
            content={
              'pay as you go gym, gym membership, gym membership ' +
              gym.location.locality +
              ', pay-as-you-go gyms in ' +
              gym.location.locality +
              ', gyms in ' +
              gym.location.locality +
              ', gym in ' +
              gym.location.locality
            }
          />
          <meta name="robots" content="follow, index" />
          <meta httpEquiv="Expires" content="15" />
          <meta name="Classification" content="Internet" />
          <meta name="Author" content="Hussle" />
          <meta name="Contact" content="info@hussle.com" />
          <meta name="Publisher" content="Hussle" />
          <meta property="fb:app_id" content="342874775786748" />
          <meta property="og:locale" content="en_GB" />
          <meta property="og:site_name" content="Hussle" />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={firstImage} />
          <meta property="og:url" content={process.env.NEXT_PUBLIC_HUSSLE_DOMAIN + gym.b2c_gym_url} />
          <meta name="apple-itunes-app" content="app-id=953242760" />
          <meta
            property="og:description"
            content={
              gym.name +
              ', read ' +
              gym.feedback.count +
              ' Hussle reviews. Visit the gym from £' +
              (gym.products && Math.min(...gym.products.map((p) => p.price)) / 100).toFixed(2)
            }
          />
          <meta property="og:title" content={gym.name} />
          <link href={process.env.NEXT_PUBLIC_HUSSLE_DOMAIN + gym.b2c_gym_url} rel="canonical" />
          <link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_STATIC_DOMAIN} />
          <link rel="icon" href={assetPrefix + '/favicon.ico'} />
          <link
            href={process.env.NEXT_PUBLIC_STATIC_DOMAIN + '/assets/fonts/EuclidCircularB/EuclidCircularB-Light.woff2'}
            rel="preload"
            type="font/woff2"
            crossOrigin=""
            as="font"
          />
          <link
            href={process.env.NEXT_PUBLIC_STATIC_DOMAIN + '/assets/fonts/EuclidCircularB/EuclidCircularB-Regular.woff2'}
            rel="preload"
            type="font/woff2"
            crossOrigin=""
            as="font"
          />
          <link
            href={process.env.NEXT_PUBLIC_STATIC_DOMAIN + '/assets/fonts/EuclidCircularB/EuclidCircularB-Medium.woff2'}
            rel="preload"
            type="font/woff2"
            crossOrigin=""
            as="font"
          />
          <link
            href={process.env.NEXT_PUBLIC_STATIC_DOMAIN + '/assets/fonts/HussleEuclid/HussleEuclid-Medium-WebM.woff2'}
            rel="preload"
            type="font/woff2"
            crossOrigin="anonymous"
            as="font"
          />
          <link href={mobileFirstImage.replace(/\.[^/.]+$/, '.webp')} rel="preload" as="image" />
          <link rel="preconnect" href="https://static.hussle.com/" />
          <link rel="preconnect" href="https://www.googletagmanager.com" />
          <link rel="preconnect" href="https://www.google-analytics.com" />
        </Head>
        <LayoutMain>
          {!isTablet && (
            <MosaicMobile
              gymName={gym.name}
              galleryData={galleryImages}
              firstImage={firstImage}
              mobileFirstImage={mobileFirstImage}
            />
          )}
          <Grid>
            <Row>
              <Column>
                <Breadcrumbs gymName={gym.name} gymUrl={gym.b2c_gym_url} gymLocation={gym.location.locality} />
                <GymHeader
                  name={gym.name}
                  address={`${gym.location.street_address}, ${gym.location.postcode}`}
                  numberOfReviews={gym.feedback.count}
                  score={gym.feedback.score}
                  activities={gym.included_access}
                  ladiesOnly={gym.ladiesonly}
                  dateLive={gym.live_at}
                />
                {!gym.is_listed && (
                  <div className={styles['gym__banners']}>
                    <Banner level="error" icon={<Info />}>
                      {gymNotListedMessage}
                    </Banner>
                  </div>
                )}
                {gym.is_temporarily_closed && gym.is_listed && (
                  <div className={styles['gym__banners']}>
                    <Banner level="info" icon={<Info />}>
                      Welcoming you back soon.
                    </Banner>
                  </div>
                )}
              </Column>
            </Row>
            <section className={styles['gym__mosaic--tablet']}>
              <Mosaic
                data={images}
                galleryData={galleryImages}
                firstImage={firstImage}
                mobileFirstImage={mobileFirstImage}
                gymName={gym.name}
              />
            </section>
          </Grid>
          <section data-testid="passpicker-container" className={styles.gym__products}>
            <PassPickerBlock featured={recommendedPass} products={gym.products} gym={gym} user={user} />
          </section>
          {isCorporateUser && (
            <>
              <HowToGetStartedCorporate />
              <Testimonials testimonials={testimonialsData} />
            </>
          )}
          <Grid>
            <Row>
              <Column xs="12" lg="10" lgOffset="1" xl="8" xlOffset="2">
                <section>
                  <ResourceList resources={gym.equipment} title="Equipment" />
                  <Divider />
                  <ResourceList resources={gym.amenities} title="Amenities" />
                </section>
              </Column>
            </Row>
            <Row className={styles.gym__location}>
              <Column xs="12" lg="6" xl="5" hd="4">
                <section className={styles.gym__opening} id="gym-details-location">
                  <SectionSubTitle className={styles['gym__opening-title']}>
                    Location &amp; Opening Hours
                  </SectionSubTitle>
                  {gym.opening_times ? (
                    <OpeningHours openingHours={gym.opening_times} fullWidth highlightColor="white" />
                  ) : (
                    <p>Opening hours are unavailable.</p>
                  )}
                  <div className={styles['gym__location-links']}>
                    <a
                      className={styles['gym__location-link']}
                      rel="noreferrer"
                      target="_blank"
                      href={`https://maps.google.com/?daddr=${gym.name}, ${gym.location.street_address}, ${gym.location.locality}, ${gym.location.postcode}`}
                    >
                      Get directions to this gym
                    </a>
                    {gym.location.what_3_words && (
                      <a
                        className={styles['gym__threewords-link']}
                        rel="noreferrer"
                        target="_blank"
                        href={`https://what3words.com/${gym.location.what_3_words}`}
                      >
                        <span className={styles.gym__threewords}>{`///`}</span>
                        {gym.location.what_3_words}
                      </a>
                    )}
                  </div>
                </section>
              </Column>
              <Column xs="12" lg="6" xl="7" hd="8">
                <section className={styles.gym__map}>
                  <Map
                    long={gym.location.longitude}
                    lat={gym.location.latitude}
                    token={`${process.env.NEXT_PUBLIC_MAPBOX_TOKEN || ''}`}
                  />
                </section>
              </Column>
            </Row>
            <Row>
              <Column xs="12" lg="10" lgOffset="1" xl="8" xlOffset="2">
                {gym.notifications && gym.notifications.length > 0 && (
                  <section className={styles.gym__notifications}>
                    <Notifications title="Please note" numberToShow={5} data={gym.notifications} />
                  </section>
                )}
                <section>
                  <SectionSubTitle>About this gym</SectionSubTitle>
                  <Description description={gym.description} />
                  <Divider />
                </section>
                {gym.is_listed && (
                  <section>
                    <GymIntro name={gym.name} products={gym.products} />
                    <Divider />
                  </section>
                )}
                <section>
                  <SectionSubTitle id="gym-details-reviews">What people are saying</SectionSubTitle>
                  <Stats
                    accessibilityScore={gym.review_stats.accessibility_score}
                    facilitiesScore={gym.review_stats.facilities_score}
                    numberOfReviews={gym.feedback.count}
                    priceScore={gym.review_stats.price_score}
                    rangeFacilityScore={gym.review_stats.range_facility_score}
                    score={gym.feedback.score}
                    serviceScore={gym.review_stats.service_score}
                  />
                  <FeedbackBlock reviews={gym.reviews} />
                </section>
              </Column>
            </Row>
          </Grid>
          <Grid>
            <Faqs gymName={gym.name} faqs={gym.faqs} />
          </Grid>
        </LayoutMain>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(makeldJsonSchema(gym)) }}
        />
      </Layout>
      <Chat />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const gymDetails = gymData.find((gym) => JSON.stringify(gym.gymUrl.split('/')) === JSON.stringify(params?.gym));

  if (!gymDetails) {
    return {
      notFound: true,
    };
  }

  const gym = await fetchGym(String(gymDetails.gymId));

  return {
    props: {
      data: gym,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: any[] = gymData.map((gym) => ({
    params: { gym: gym.gymUrl.split('/') },
  }));

  return {
    paths,
    fallback: false,
  };
};

export { GymDetailsPage as default };
